import { conversationInvitationIcon, toClassicIcon } from "common/defaultIcons";
import {
  LAYOUTS,
  MENU_SECTIONS,
  MODULE_SETTINGS,
  TEACHER_ROLES,
} from "shared/constants";
import type { AppRouteRecordRaw } from "shared/types";
import { CONVERSATION_TAB } from "conversations/types";
import { CONVERSATION_ROUTE_NAMES } from "./routeNames";

/**
 * One tree under `/conversations`: the parent is the menu entry and redirects to the
 * overview, its first child, and every other gesprek screen is a child of it — the way Mijn
 * portfolio is built. The parent is named so the menu can link to it, and redirects
 * because a named parent with an empty-path child does not render that child on its own.
 *
 * Third in "Organiseren" — Besprekingen, Actieplannen, Gesprekken, Taken, Leerdoelen. It
 * replaces the two placeholder lists CATWISE-1172 shipped (`/conversations` and
 * `/conversation-invitations`): both kinds are rows of this one screen now, so the
 * invitations route is gone with no redirect — it only ever existed on a feature branch.
 *
 * The menu entry is a dropdown with "Gesprekkenoverzicht" under it, so Gesprekken reads
 * the same as Taken and Actieplannen (#1185). It has one child for now: a Gesprekkenbibliotheek
 * is named in #1166 as future work and the gesprekstypes live under Beheren →
 * Instellingen, so there is nothing else to link to yet.
 *
 * The Actief/Afgerond split is a route per tab, with the overview redirecting to the
 * first — the same structure the leerlingoverzicht already has, and the one every tab
 * group in the app uses. Each tab route has its own page component so a switch remounts
 * the page and `FilterPanel` re-initializes its query params; see "Tab routes" in
 * `.claude/docs/frontend_architecture.md`. The overview also maps the old `?status=`
 * value onto its tab route, so links and bookmarks from before the split still land right.
 *
 * The children repeat `roleRequired` and `settingRequired`: `isAuthorized()` reads
 * `settingRequired` off the deepest matched record only, so a child without it is a way in
 * that the parent's gate never sees. Gated on the gesprekken setting and never on a
 * module — CONVERSATION-SETTING-ONLY-GATE-001. The API read path is ungated, so hiding the
 * screens is what a school with gesprekken switched off actually sees.
 */
const routes: AppRouteRecordRaw[] = [
  {
    name: CONVERSATION_ROUTE_NAMES.INDEX,
    path: "/conversations",
    component: () => import("views/EmptyView.vue"),
    redirect: { name: CONVERSATION_ROUTE_NAMES.OVERVIEW },
    meta: {
      title: "Gesprekken",
      roleRequired: TEACHER_ROLES,
      // The same glyph the rows of the overview carry, per the design — not the
      // generic chat icon the two CATWISE-1172 placeholders shared.
      icon: toClassicIcon(conversationInvitationIcon),
      isMenuItem: true,
      // A dropdown, like Taken and Actieplannen: the entry itself opens the overview, and
      // "Gesprekkenoverzicht" sits under it. `MenuLink` renders a dropdown as soon as a
      // child is a menu item of the same section, and vue-router keeps the parent's link
      // active while any child is open because this record stays in `route.matched`.
      inDropdown: true,
      menuSection: MENU_SECTIONS.ORGANISE,
      menuOrder: 3,
      settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
    },
    children: [
      {
        name: CONVERSATION_ROUTE_NAMES.OVERVIEW,
        path: "overview",
        component: () => import("views/EmptyView.vue"),
        // `?status=completed` was how the tabs were switched before they became routes.
        // The value is dropped on the way so FilterPanel never sees it as a filter.
        redirect: (to) => ({
          name:
            to.query.status === CONVERSATION_TAB.COMPLETED
              ? CONVERSATION_ROUTE_NAMES.OVERVIEW_COMPLETED
              : CONVERSATION_ROUTE_NAMES.OVERVIEW_PLANNED,
          query: { ...to.query, status: undefined },
        }),
        meta: {
          title: "Gesprekkenoverzicht",
          // NO_WRAPPER: the breadcrumb, the title, the tabs and the filters sit on the page
          // background and only the table is a card, so the layout's single white panel
          // would nest a card in a card. Same reason as the leerlingoverzicht.
          layout: LAYOUTS.MAIN_NO_WRAPPER,
          roleRequired: TEACHER_ROLES,
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
          isMenuItem: true,
          inDropdown: true,
          menuSection: MENU_SECTIONS.ORGANISE,
          // The pages render their own heading and their own tab row.
          hideTitle: true,
          hideSubmenu: true,
        },
        children: [
          {
            name: CONVERSATION_ROUTE_NAMES.OVERVIEW_PLANNED,
            path: "planned",
            component: () =>
              import("conversations/pages/TeacherPlannedConversationsPage.vue"),
            meta: {
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
            },
          },
          {
            name: CONVERSATION_ROUTE_NAMES.OVERVIEW_COMPLETED,
            path: "completed",
            component: () =>
              import(
                "conversations/pages/TeacherCompletedConversationsPage.vue"
              ),
            meta: {
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
            },
          },
        ],
      },
      {
        name: CONVERSATION_ROUTE_NAMES.INVITATION_CREATE,
        // Under its own `invitation` segment so a bare id below `/conversations/` stays a
        // gesprek — the same split the class fragments make.
        path: "invitation/create",
        component: () =>
          import(
            "conversations/pages/TeacherConversationInvitationCreatePage.vue"
          ),
        meta: {
          title: "Gesprek inplannen",
          // NO_WRAPPER: MainLayout otherwise wraps the whole page in one white panel,
          // which would swallow the four cards into a single card and pull the heading
          // inside it. This form brings its own cards.
          layout: LAYOUTS.MAIN_NO_WRAPPER,
          roleRequired: TEACHER_ROLES,
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
          // Reached from the Gesprekken overview, not from the menu.
          isMenuItem: false,
          // ContentPageLayout renders the heading, with the actions beside it.
          hideTitle: true,
        },
      },
      {
        name: CONVERSATION_ROUTE_NAMES.INVITATION_EDIT,
        // Digits only, like the detail route below it: `invitation/create` is static and
        // must never be read as an invitation id.
        path: "invitation/:invitationId(\\d+)/edit",
        component: () =>
          import(
            "conversations/pages/TeacherConversationInvitationEditPage.vue"
          ),
        meta: {
          title: "Gesprek bewerken",
          // Same reason as the create page this reuses: MainLayout would otherwise wrap
          // the four cards into one white panel.
          layout: LAYOUTS.MAIN_NO_WRAPPER,
          roleRequired: TEACHER_ROLES,
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
          // Reached from the gesprek's own detail screen, not from the menu.
          isMenuItem: false,
          // ContentPageLayout renders the heading, with the actions beside it.
          hideTitle: true,
        },
      },
      {
        name: CONVERSATION_ROUTE_NAMES.INVITATION_DETAIL,
        // Digits only, and last of the `invitation` routes: the create path above is
        // static and must never be read as an invitation id.
        path: "invitation/:invitationId(\\d+)",
        component: () =>
          import(
            "conversations/pages/TeacherConversationInvitationDetailPage.vue"
          ),
        meta: {
          title: "Gesprek",
          layout: LAYOUTS.MAIN,
          roleRequired: TEACHER_ROLES,
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
          // Reached by clicking an ingepland gesprek, not from the menu.
          isMenuItem: false,
          // The page brings its own header, its own KPI row and its own cards, so the
          // layout's single white panel would nest a card in a card.
          hideTitle: true,
          hideBackground: true,
        },
      },
      {
        name: CONVERSATION_ROUTE_NAMES.DETAIL,
        path: ":conversationId",
        component: () =>
          import("conversations/pages/TeacherConversationDetailPage.vue"),
        meta: {
          title: "Gesprek",
          layout: LAYOUTS.MAIN,
          roleRequired: TEACHER_ROLES,
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
          // No menu item of its own: this is reached by clicking a gesprek row.
          isMenuItem: false,
          // The page renders its own header — actions, title, badges, datum and the
          // leerling — and its own white card per block, so the layout's single panel
          // would nest a card in a card. Same shape as the leerling's own detail page.
          hideTitle: true,
          hideBackground: true,
        },
      },
    ],
  },
];

export default routes;
